import React, { useState, useEffect } from 'react';
import { X, User, Calendar, CheckCircle } from 'lucide-react';
import { questionnaireService } from '../services/questionnaireService';
import { templateService } from '../services/templateService';
import type { QuestionnaireResponse, QuestionnaireAssignment, QuestionnaireTemplate } from '../types/questionnaires';

interface QuestionnaireResponseViewProps {
  assignment: QuestionnaireAssignment;
  onClose: () => void;
}

const QuestionnaireResponseView: React.FC<QuestionnaireResponseViewProps> = ({ 
  assignment, 
  onClose 
}) => {
  const [response, setResponse] = useState<QuestionnaireResponse | null>(null);
  const [template, setTemplate] = useState<QuestionnaireTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadResponse();
  }, [assignment]);

  const loadResponse = async () => {
    if (!assignment.assignedToUserId) {
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Load response
      const data = await questionnaireService.getResponse(
        assignment.id, 
        assignment.assignedToUserId
      );
      setResponse(data);
      
      // Load template
      if (data) {
        const templates = await templateService.listTemplates();
        const responseTemplate = templates.find(t => t.id === data.templateId);
        setTemplate(responseTemplate || null);
      }
    } catch (error) {
      console.error('Error loading response:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Antwort ansehen</h2>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                <span>{assignment.assignedTo}</span>
              </div>
              {response?.submittedAt && (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span>Abgeschickt: {formatDate(response.submittedAt)}</span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : !response ? (
            <div className="text-center py-12 text-gray-500">
              <p>Noch keine Antwort vorhanden</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Project count */}
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-purple-900 font-medium">
                  {(response.projects || []).length} Projekt{(response.projects || []).length !== 1 ? 'e' : ''} eingereicht
                </p>
              </div>

              {/* Render each project with template fields */}
              {(response.projects || []).map((project, index) => (
                <div key={index} className="border-2 border-purple-200 rounded-xl p-6 bg-white">
                  <h3 className="text-2xl font-bold text-purple-900 mb-6 pb-3 border-b-2 border-purple-200">
                    Projekt {index + 1}
                    {project.fieldValues?.['projektTitel'] && (
                      <span className="text-lg font-normal text-gray-600 ml-3">
                        - {project.fieldValues['projektTitel']}
                      </span>
                    )}
                  </h3>

                  {/* Dynamic field rendering */}
                  <div className="space-y-4">
                    {template?.fields
                      .sort((a, b) => a.order - b.order)
                      .map(field => {
                        const value = project.fieldValues?.[field.id];
                        if (!value) return null;
                        
                        return (
                          <div key={field.id} className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-2">{field.label}</h4>
                            {field.id === 'materialLink' && value.startsWith('http') ? (
                              <a 
                                href={value} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-purple-600 hover:text-purple-700 underline"
                              >
                                {value}
                              </a>
                            ) : (
                              <p className="text-gray-700 whitespace-pre-wrap">{value}</p>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}

              {/* Metadata */}
              <div className="mt-6 pt-6 border-t border-gray-200 text-sm text-gray-600">
                <div className="flex items-center gap-6">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>Zuletzt gespeichert: {formatDate(response.lastSaved)}</span>
                  </div>
                  {response.submittedAt && (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      <span>Abgeschickt: {formatDate(response.submittedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireResponseView;

